import { Controller } from "@hotwired/stimulus"
import { showConfirmModal } from "../lib/custom_confirm"

// Confirmation for a form Turbo does NOT handle.
//
// `data: { turbo_confirm: ... }` is the normal way to guard a destructive
// submit — application.js routes it through showConfirmModal. But it only works
// while Turbo intercepts the submit. A form marked `turbo: false` is submitted
// natively, so Turbo.config.forms.confirm never runs and the attribute is
// silently inert: the destructive action fires with no prompt at all.
//
// That combination is not rare here. A form renders its response INLINE (rather
// than redirecting) precisely when the response carries something that must not
// ride a flash or a URL — one-time backup codes, for instance — and Turbo
// discards a 200 HTML response to a form submission, which forces `turbo: false`.
// So the very forms most worth confirming are the ones that cannot use
// turbo_confirm.
//
// Because the modal is Promise-based and submit is synchronous, the original
// submit is always prevented and re-issued on accept, guarded so the re-issued
// submit is not prompted again.
//
//   = form_with url: ..., data: { turbo: false, controller: 'confirm-submit',
//       action: 'submit->confirm-submit#confirm',
//       confirm_submit_message_value: 'Are you sure?' }
//
// Optional `title` and `variant` ('danger' | 'warning' | 'success' |
// 'primary') values override showConfirmModal's wording-based detection —
// for a destructive confirmation whose message names no destructive verb
// (Phone Settings "Release Number", #15113). Blank keeps the detection.
// An optional `cancelLabel` names the safe button when a bare "Cancel" would
// read like the action ("Keep Refills" beside "Cancel Refills", #19616).
export default class extends Controller {
  static values = {
    message: String,
    confirmLabel: { type: String, default: "Continue" },
    cancelLabel: { type: String, default: "" },
    title: { type: String, default: "" },
    variant: { type: String, default: "" },
  }

  #confirmed = false
  #prompting = false

  #modalOptions () {
    const options = { confirmText: this.confirmLabelValue }
    if (this.titleValue) options.title = this.titleValue
    if (this.variantValue) options.variant = this.variantValue
    if (this.cancelLabelValue) options.cancelText = this.cancelLabelValue
    return options
  }

  confirm (event) {
    // The re-entrant submit issued by requestSubmit() below. Let it through
    // rather than prompting a second time.
    if (this.#confirmed) {
      this.#confirmed = false
      return
    }

    event.preventDefault()

    // A second submit while the modal is still open — a double click, or Enter
    // held down — would stack a second dialog over the first, and each pending
    // promise would resolve into its own requestSubmit. One prompt at a time.
    if (this.#prompting) return

    this.#prompting = true

    showConfirmModal(this.messageValue, this.#modalOptions())
      // Two-argument then(), NOT .then().catch(): a trailing .catch() also
      // catches anything thrown INSIDE the fulfilment handler, which would
      // report a requestSubmit failure as a dialog failure. onRejected here
      // fires only for the dialog's own rejection.
      .then(
        (accepted) => {
          // isConnected mirrors app_setting_confirm_controller: the modal is
          // async, so by the time it resolves the form may already be gone (a
          // Turbo render elsewhere, or a navigation this submit started).
          // Calling requestSubmit on a detached element submits nothing and
          // masks the real state.
          if (!accepted || !this.element.isConnected) return

          this.#confirmed = true

          try {
            // requestSubmit, not submit(): submit() skips validation AND does
            // not fire a submit event, so the guard above would never see the
            // re-entry.
            this.element.requestSubmit()
          } catch (error) {
            // The pass-through guard is only safe to leave raised if the submit
            // it excuses actually happened. Stuck at true, the NEXT submit
            // returns early and skips the confirmation entirely — a silent
            // bypass on a destructive action.
            this.#confirmed = false
            // Reported under its own message, not the dialog's: this is the
            // whole reason for the two-argument then() above. Not re-thrown —
            // nothing downstream handles it, so it would surface as an
            // unhandled rejection, which is noisier than the mislabelling it
            // would fix.
            console.error("confirm-submit: submitting the confirmed form failed", error)
          }
        },
        (error) => {
          // Fail CLOSED: the submit is destructive, so a dialog that never
          // appeared must not be treated as consent. Surfaced rather than
          // swallowed, because the next click will hit the same failure.
          console.error("confirm-submit: confirmation dialog failed", error)
        }
      )
      // finally, NOT the first line of then(). #prompting is set BEFORE the
      // promise settles, so anything that leaves it unsettled latches it on and
      // silently swallows every later submit — no dialog, no submit, no error,
      // until a reload. showConfirmModal builds its Bootstrap modal inside the
      // Promise executor (gl_modal_shell.js `new window.bootstrap.Modal`), so a
      // throw there really does surface as a rejection. #confirmed never needed
      // this because it is only ever set AFTER the promise settles.
      .finally(() => {
        this.#prompting = false
      })
  }
}
